import {useState} from "react";
import {
    Box, Stack, Typography, Card, CardContent, Button, TextField,
    Select, MenuItem, Slider, Switch, Chip, Divider, CircularProgress,
} from "@mui/material";
import {
    IconServer, IconRefresh, IconDeviceFloppy, IconCircleCheck,
    IconCircleX, IconTerminal2, IconCpu,
} from "@tabler/icons-react";
import {toast} from "react-toastify";
import {KnowledgeDatabaseRepository} from "../../../../repositories/KnowledgeDatabaseRepository";
import {STATUS_COLORS} from "../../../../config/statusColors";

const cardSx = {borderRadius: 2, border: "1px solid", borderColor: "divider", boxShadow: "none"};

const inputSx = {
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {borderColor: "#000"},
    "& .MuiInputLabel-root.Mui-focused": {color: "#000"},
};

const sliderSx = {
    color: "#000",
    "& .MuiSlider-thumb": {width: 14, height: 14},
};

const blackBtn = {
    bgcolor: "#000", color: "#fff", textTransform: "none", borderRadius: 2,
    boxShadow: "none", "&:hover": {bgcolor: "#000"},
};

const statusStyle = (status) => {
    if (status === "online") return {bg: STATUS_COLORS.success.bg, color: STATUS_COLORS.success.text};
    if (status === "connecting") return {bg: STATUS_COLORS.warning.bg, color: STATUS_COLORS.warning.text};
    return {bg: STATUS_COLORS.error.bg, color: STATUS_COLORS.error.text};
};

const ENDPOINTS = [
    {method: "GET", path: "/health", desc: "Server health check"},
    {method: "POST", path: "/documents/file", desc: "Upload file document"},
    {method: "DELETE", path: "/documents/{doc_id}", desc: "Delete specific document"},
    {method: "POST", path: "/query", desc: "Query knowledge base"},
    {method: "GET", path: "/graphs", desc: "Get knowledge graph"},
    {method: "PUT", path: "/config", desc: "Update server configuration"},
];

const methodColor = (method) => {
    if (method === "GET") return STATUS_COLORS.success.text;
    if (method === "POST") return STATUS_COLORS.info.text;
    if (method === "PUT") return STATUS_COLORS.warning.text;
    return STATUS_COLORS.error.text;
};

export const ServerConfig = ({topic, onUpdated}) => {
    const srv = topic.server;
    const [host, setHost] = useState(srv?.host ?? "localhost");
    const [port, setPort] = useState(String(srv?.port ?? 9621));
    const [apiKey, setApiKey] = useState("");
    const [workingDir, setWorkingDir] = useState(srv?.workingDir ?? "");
    const [isChecking, setIsChecking] = useState(false);
    const [isSavingServer, setIsSavingServer] = useState(false);
    const [isSavingConfig, setIsSavingConfig] = useState(false);

    const [localConfig, setLocalConfig] = useState({...topic.config});

    const handleHealthCheck = async () => {
        setIsChecking(true);
        try {
            await KnowledgeDatabaseRepository.checkHealth(topic.id);
            toast.success("Health check complete");
            onUpdated?.();
        } catch {
            toast.error("Health check failed");
        } finally {
            setIsChecking(false);
        }
    };

    const handleSaveServer = async () => {
        setIsSavingServer(true);
        const portNum = parseInt(port) || 9621;
        try {
            await KnowledgeDatabaseRepository.upsertServer(topic.id, {
                host,
                port: portNum,
                apiBase: `http://${host}:${portNum}`,
                apiKey: apiKey || undefined,
                workingDir: workingDir || undefined,
            });
            setApiKey("");
            toast.success("Server configuration saved");
            onUpdated?.();
        } catch (err) {
            toast.error(err.message ?? "Failed to save server configuration");
        } finally {
            setIsSavingServer(false);
        }
    };

    const handleSaveConfig = async () => {
        setIsSavingConfig(true);
        try {
            await KnowledgeDatabaseRepository.upsertConfig(topic.id, localConfig);
            toast.success("LightRAG configuration saved");
            onUpdated?.();
        } catch (err) {
            toast.error(err.message ?? "Failed to save configuration");
        } finally {
            setIsSavingConfig(false);
        }
    };

    const status = topic.server?.status ?? "offline";
    const portNum = parseInt(port) || 9621;
    const style = statusStyle(status);

    const statusIcon = status === "online"
        ? <IconCircleCheck size={16} color={STATUS_COLORS.success.text}/>
        : status === "connecting"
            ? <CircularProgress size={14}/>
            : <IconCircleX size={16} color={STATUS_COLORS.error.text}/>;

    return (
        <Stack gap={2.5}>

            {/* Server Connection */}
            <Card sx={cardSx}>
                <Box sx={{px: 3, pt: 2.5, pb: 1}}>
                    <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                            <IconServer size={16}/>
                            <Typography fontWeight={600} fontSize={15}>Server Connection</Typography>
                        </Stack>
                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                            {statusIcon}
                            <Chip label={status} size={"small"} sx={{fontSize: 10, height: 20, bgcolor: style.bg, color: style.color}}/>
                        </Stack>
                    </Stack>
                </Box>
                <CardContent sx={{pt: 1}}>
                    <Stack gap={2.5}>
                        <Stack direction={"row"} gap={2}>
                            <Box sx={{flex: 1}}>
                                <Typography fontSize={12} sx={{mb: 0.75}}>Host</Typography>
                                <TextField fullWidth size={"small"} value={host} onChange={(e) => setHost(e.target.value)} sx={inputSx}/>
                            </Box>
                            <Box sx={{flex: 1}}>
                                <Typography fontSize={12} sx={{mb: 0.75}}>Port</Typography>
                                <TextField fullWidth size={"small"} type={"number"} value={port} onChange={(e) => setPort(e.target.value)} sx={inputSx}/>
                            </Box>
                        </Stack>

                        <Box>
                            <Typography fontSize={12} sx={{mb: 0.75}}>API Key</Typography>
                            <TextField
                                fullWidth size={"small"} type={"password"} value={apiKey}
                                onChange={(e) => setApiKey(e.target.value)}
                                placeholder={topic.server?.hasApiKey ? "Key stored — enter new value to replace" : "lrag-key-xxxxx (optional)"}
                                sx={inputSx}
                            />
                            {topic.server?.hasApiKey && (
                                <Typography fontSize={11} color={"text.secondary"} sx={{mt: 0.5}}>
                                    A key is stored encrypted. Leave blank to keep the existing key.
                                </Typography>
                            )}
                        </Box>

                        <Box>
                            <Typography fontSize={12} sx={{mb: 0.75}}>Working Directory</Typography>
                            <TextField fullWidth size={"small"} value={workingDir} onChange={(e) => setWorkingDir(e.target.value)} sx={inputSx}/>
                        </Box>

                        <Box sx={{bgcolor: "action.hover", borderRadius: 1.5, p: 1.5}}>
                            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                                <Typography fontSize={12} color={"text.secondary"}>API Base URL</Typography>
                                <Box component={"code"} sx={{bgcolor: "action.selected", px: 1, py: 0.25, borderRadius: 0.5, fontSize: 11}}>
                                    http://{host}:{portNum}
                                </Box>
                            </Stack>
                        </Box>

                        <Stack direction={"row"} gap={1.5}>
                            <Button
                                variant={"ghost"}
                                startIcon={isChecking ? <CircularProgress size={13}/> : <IconRefresh size={14}/>}
                                onClick={handleHealthCheck}
                                disabled={isChecking || !topic.server}
                                sx={{textTransform: "none", fontSize: 12, borderColor: "divider", color: "text.primary"}}
                            >
                                Health Check
                            </Button>
                            <Button
                                startIcon={isSavingServer ? <CircularProgress size={13} sx={{color: "#fff"}}/> : <IconDeviceFloppy size={14}/>}
                                onClick={handleSaveServer}
                                disabled={isSavingServer}
                                sx={{...blackBtn, fontSize: 12}}
                            >
                                Save Connection
                            </Button>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            {/* LightRAG Configuration */}
            <Card sx={cardSx}>
                <Box sx={{px: 3, pt: 2.5, pb: 1}}>
                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                        <IconCpu size={16}/>
                        <Typography fontWeight={600} fontSize={15}>LightRAG Configuration</Typography>
                    </Stack>
                </Box>
                <CardContent sx={{pt: 1}}>
                    <Stack gap={3}>

                        <Stack direction={"row"} gap={2}>
                            <Box sx={{flex: 1}}>
                                <Typography fontSize={12} sx={{mb: 0.75}}>LLM Model</Typography>
                                <Select
                                    fullWidth size={"small"}
                                    value={localConfig.llmModel}
                                    onChange={(e) => setLocalConfig({...localConfig, llmModel: e.target.value})}
                                >
                                    <MenuItem value={"gpt-4o-mini"}>GPT-4o Mini</MenuItem>
                                    <MenuItem value={"gpt-4o"}>GPT-4o</MenuItem>
                                    <MenuItem value={"gpt-4-turbo"}>GPT-4 Turbo</MenuItem>
                                    <MenuItem value={"claude-3-sonnet"}>Claude 3 Sonnet</MenuItem>
                                    <MenuItem value={"claude-3-haiku"}>Claude 3 Haiku</MenuItem>
                                    <MenuItem value={"llama-3.1-70b"}>Llama 3.1 70B</MenuItem>
                                    <MenuItem value={"ollama/custom"}>Ollama (Custom)</MenuItem>
                                </Select>
                            </Box>
                            <Box sx={{flex: 1}}>
                                <Typography fontSize={12} sx={{mb: 0.75}}>Embedding Model</Typography>
                                <Select
                                    fullWidth size={"small"}
                                    value={localConfig.embeddingModel}
                                    onChange={(e) => setLocalConfig({...localConfig, embeddingModel: e.target.value})}
                                >
                                    <MenuItem value={"text-embedding-3-small"}>text-embedding-3-small</MenuItem>
                                    <MenuItem value={"text-embedding-3-large"}>text-embedding-3-large</MenuItem>
                                    <MenuItem value={"text-embedding-ada-002"}>text-embedding-ada-002</MenuItem>
                                    <MenuItem value={"nomic-embed-text"}>nomic-embed-text</MenuItem>
                                </Select>
                            </Box>
                        </Stack>

                        <Divider/>

                        <Box>
                            <Typography fontSize={12} color={"text.secondary"} sx={{mb: 1.5}}>Chunking Settings</Typography>
                            <Stack direction={"row"} gap={4}>
                                <Box sx={{flex: 1}}>
                                    <Typography fontSize={12} sx={{mb: 1}}>Chunk Size: {localConfig.chunkSize}</Typography>
                                    <Slider
                                        value={localConfig.chunkSize}
                                        onChange={(e, v) => setLocalConfig({...localConfig, chunkSize: v})}
                                        min={200} max={4000} step={100} size={"small"} sx={sliderSx}
                                    />
                                </Box>
                                <Box sx={{flex: 1}}>
                                    <Typography fontSize={12} sx={{mb: 1}}>Chunk Overlap: {localConfig.chunkOverlap}</Typography>
                                    <Slider
                                        value={localConfig.chunkOverlap}
                                        onChange={(e, v) => setLocalConfig({...localConfig, chunkOverlap: v})}
                                        min={0} max={500} step={10} size={"small"} sx={sliderSx}
                                    />
                                </Box>
                            </Stack>
                        </Box>

                        <Divider/>

                        <Box>
                            <Typography fontSize={12} color={"text.secondary"} sx={{mb: 1.5}}>Generation Settings</Typography>
                            <Stack direction={"row"} gap={4}>
                                <Box sx={{flex: 1}}>
                                    <Typography fontSize={12} sx={{mb: 1}}>Max Tokens: {localConfig.maxTokens}</Typography>
                                    <Slider
                                        value={localConfig.maxTokens}
                                        onChange={(e, v) => setLocalConfig({...localConfig, maxTokens: v})}
                                        min={256} max={16384} step={256} size={"small"} sx={sliderSx}
                                    />
                                </Box>
                                <Box sx={{flex: 1}}>
                                    <Typography fontSize={12} sx={{mb: 1}}>Temperature: {localConfig.temperature.toFixed(2)}</Typography>
                                    <Slider
                                        value={localConfig.temperature * 100}
                                        onChange={(e, v) => setLocalConfig({...localConfig, temperature: v / 100})}
                                        min={0} max={200} step={5} size={"small"} sx={sliderSx}
                                    />
                                </Box>
                            </Stack>
                        </Box>

                        <Divider/>

                        <Box>
                            <Typography fontSize={12} color={"text.secondary"} sx={{mb: 1.5}}>Graph Settings</Typography>
                            <Stack gap={2.5}>
                                <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                                    <Box>
                                        <Typography fontSize={12}>Enable Graph Search</Typography>
                                        <Typography fontSize={11} color={"text.secondary"}>
                                            Use knowledge graph for enhanced retrieval
                                        </Typography>
                                    </Box>
                                    <Switch
                                        checked={localConfig.enableGraphSearch}
                                        onChange={(e) => setLocalConfig({...localConfig, enableGraphSearch: e.target.checked})}
                                    />
                                </Stack>
                                <Box>
                                    <Typography fontSize={12} sx={{mb: 1}}>Community Level: {localConfig.graphCommunityLevel}</Typography>
                                    <Slider
                                        value={localConfig.graphCommunityLevel}
                                        onChange={(e, v) => setLocalConfig({...localConfig, graphCommunityLevel: v})}
                                        min={1} max={5} step={1} size={"small"} sx={sliderSx}
                                    />
                                </Box>
                                <Box>
                                    <Typography fontSize={12} sx={{mb: 1}}>Top K: {localConfig.topK}</Typography>
                                    <Slider
                                        value={localConfig.topK}
                                        onChange={(e, v) => setLocalConfig({...localConfig, topK: v})}
                                        min={1} max={200} step={1} size={"small"} sx={sliderSx}
                                    />
                                </Box>
                            </Stack>
                        </Box>

                        <Box>
                            <Button
                                startIcon={isSavingConfig ? <CircularProgress size={13} sx={{color: "#fff"}}/> : <IconDeviceFloppy size={14}/>}
                                onClick={handleSaveConfig}
                                disabled={isSavingConfig}
                                sx={{...blackBtn, fontSize: 12}}
                            >
                                Save Configuration
                            </Button>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>

            {/* API Endpoints Reference */}
            <Card sx={cardSx}>
                <Box sx={{px: 3, pt: 2.5, pb: 1}}>
                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                        <IconTerminal2 size={16}/>
                        <Typography fontWeight={600} fontSize={15}>LightRAG API Endpoints</Typography>
                    </Stack>
                </Box>
                <CardContent sx={{pt: 1}}>
                    <Stack gap={0.5}>
                        {ENDPOINTS.map(ep => (
                            <Stack
                                key={ep.path + ep.method}
                                direction={"row"} alignItems={"center"} gap={1.5}
                                sx={{p: 1, borderRadius: 1, "&:hover": {bgcolor: "action.hover"}}}
                            >
                                <Chip
                                    label={ep.method} variant={"outlined"} size={"small"}
                                    sx={{
                                        fontSize: 9, height: 18, width: 56, justifyContent: "center",
                                        color: methodColor(ep.method), borderColor: methodColor(ep.method),
                                    }}
                                />
                                <Box component={"code"} sx={{fontSize: 11, flex: 1}}>{ep.path}</Box>
                                <Typography fontSize={11} color={"text.secondary"}>{ep.desc}</Typography>
                            </Stack>
                        ))}
                    </Stack>
                </CardContent>
            </Card>

        </Stack>
    );
};